import axios from 'axios'

import appConfig from 'configs/app.config'
import {TOKEN_TYPE, REQUEST_HEADER_AUTH_KEY} from 'constants/api.constant'
import {pushAlert} from 'components/ui'
import store from 'store'
import {clearAdminClub, setClub} from 'store/auth/adminClubSlice'
import {onSignOutSuccess, setAccessToken} from 'store/auth/sessionSlice'
import {setUser, userLoggedOut} from 'store/auth/userSlice'
import {
    getAccessTokenFromAuthPayload,
    getAuthPayload,
    getClubFromAuthPayload,
    getUserFromAuthPayload,
} from 'utils/authSession'

const unauthorizedCode = [401]

const BaseService = axios.create({
    baseURL: appConfig.apiPrefix,
    withCredentials: true,
    headers: {
        'Access-Control-Allow-Origin': '*',
    },
})

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
    failedQueue.forEach(({resolve, reject}) => {
        if (error) {
            reject(error)
        } else {
            resolve(token)
        }
    })

    failedQueue = []
}

const getAccessToken = () => store.getState()?.auth?.session?.accessToken

const isSessionEndpoint = (url = '') => url.includes('/admin-sessions')

const clearSession = () => {
    store.dispatch(onSignOutSuccess())
    store.dispatch(userLoggedOut())
    store.dispatch(clearAdminClub())
}

const applyRefreshedSession = (response) => {
    const payload = getAuthPayload(response?.data)
    const accessToken = getAccessTokenFromAuthPayload(payload)
    const user = getUserFromAuthPayload(payload)
    const club = getClubFromAuthPayload(payload)

    if (!accessToken) {
        return ''
    }

    store.dispatch(setAccessToken({accessToken}))

    if (user) {
        store.dispatch(setUser(user))
    }

    if (club) {
        store.dispatch(setClub(club))
    }

    return accessToken
}

const interceptorsRequest = [
    (request) => {
        const accessToken = getAccessToken()

        if (accessToken) {
            request.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`
        }

        return request
    },
    (err) => Promise.reject(err),
]

const interceptorsResponse = [
    (response) => response.data,
    async (error) => {
        const {response, config} = error

        if (!config) {
            return Promise.reject(error)
        }

        const statusCode = response?.data?.code || response?.data?.statusCode || response?.status
        const isUnauth = unauthorizedCode.includes(statusCode) || statusCode === 'UNAUTHORIZED_ERROR'

        if (isSessionEndpoint(config.url)) {
            return Promise.reject(response?.data || error)
        }

        if (!isUnauth) {
            return Promise.reject(response?.data || error)
        }

        if (config._retry) {
            return Promise.reject(response?.data || error)
        }

        config._retry = true

        if (isRefreshing) {
            return new Promise((resolve, reject) => {
                failedQueue.push({resolve, reject})
            })
                .then((newAccessToken) => {
                    config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${newAccessToken}`
                    return BaseService(config)
                })
                .catch((err) => Promise.reject(err))
        }

        isRefreshing = true

        try {
            const refreshResponse = await axios.put(`${appConfig.apiPrefix}/admin-sessions`, undefined, {
                withCredentials: true,
            })
            const accessToken = applyRefreshedSession(refreshResponse)

            if (!accessToken) {
                throw new Error('Missing access token in refresh response')
            }

            processQueue(null, accessToken)

            config.headers[REQUEST_HEADER_AUTH_KEY] = `${TOKEN_TYPE}${accessToken}`

            return BaseService(config)
        } catch (err) {
            processQueue(err, null)

            pushAlert('danger', 'The current session has expired')
            clearSession()

            return Promise.reject(err)
        } finally {
            isRefreshing = false
        }
    },
]

BaseService.interceptors.request.use(...interceptorsRequest)
BaseService.interceptors.response.use(...interceptorsResponse)

export default BaseService
